// frontend/src/telephony/activeCallRecording/ActiveCallRecordingCard.tsx
//
// The Recording card inside the active-call UI. Renders ONLY when the
// call is connected/answered.
//
// UX principles (polish pass):
//   • No configuration surfaced during a live call. The user's default
//     mode belongs in Settings, not here.
//   • Recording status is the most important thing on screen — larger
//     dot, prominent elapsed timer, generous spacing.
//   • Verbs the user understands: Start Recording, Stop Recording,
//     Resume Recording. Never "segment", "session", "mode", "state".
//   • Stopped state points at THIS call, not previous artifacts:
//     "Last Recording · 3m 42s" as a subtle line, one-click Resume.
//
// States (all authoritative from /recording/effective-state):
//   1. Compliance NEVER            "Recording Disabled" — no actions.
//   2. Compliance ALWAYS (locked)  Big red pulsing dot + timer, no actions.
//   3. Idle (never recorded yet)   "Not Recording" + [Start Recording]
//   4. Recording                   Big red pulsing dot + prominent timer + [Stop Recording]
//   5. Stopped (recorded earlier)  "Not Recording" + Last Recording · Xm Ys + [Resume Recording]

import { useEffect, useState } from 'react';
import { useActiveCallRecording } from './useActiveCallRecording';
import { useGetRecordingEffectiveStateQuery } from '../../api/telephonyApi';

interface Props {
  callSid: string | null;
  workspaceRecordingMode: 'off' | 'always' | undefined;
  activeCallKind: string;
  /**
   * Phase 1 linking — the CRM's own outboundCallId for the currently
   * active Outbound-managed call. Forwarded to `useActiveCallRecording`
   * which sends it to the backend so the new CallRecording is linked to
   * the placeholder CallSession at creation time. Absent for CRM-native
   * calls (no-op).
   */
  outboundCallId?: string | null;
}

function formatDurationClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const s = Math.floor(seconds);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (hh > 0) {
    return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function formatDurationHuman(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0s';
  const s = Math.floor(seconds);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (hh > 0) return `${hh}h ${mm}m ${ss}s`;
  if (mm > 0) return `${mm}m ${ss}s`;
  return `${ss}s`;
}

/**
 * Elapsed seconds since an anchor timestamp (ms). Returns 0 when
 * anchor is null. Ticks every second while a valid anchor is present.
 */
function useRecordingDuration(anchorMs: number | null): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (anchorMs == null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [anchorMs]);
  if (anchorMs == null) return 0;
  return Math.max(0, Math.floor((now - anchorMs) / 1000));
}

/**
 * Resolve the elapsed-time anchor.
 *
 * Prefers the server-provided `effective.startedAt` (populated once a
 * CRM CallRecording row exists for the call). Falls back to a client-
 * side anchor captured when the call first goes connected AND the
 * frozen recording policy is `automatic` — this is the TwiML-owned
 * automatic path, where the backend does NOT create a CallRecording
 * row upfront (see `callRecordingController.ts:237-249` twimlOwnsAutomatic
 * early return). Without this fallback, the elapsed counter is stuck
 * at 00:00 for the entire call even though TwiML is actively recording
 * at Twilio's side.
 *
 * The client anchor resets when the call ends, so the next call gets
 * a fresh timer.
 */
function useElapsedAnchor(opts: {
  isConnected: boolean;
  mode: 'automatic' | 'manual' | 'never' | undefined;
  serverStartedAt: string | null | undefined;
}): number | null {
  const [clientAnchor, setClientAnchor] = useState<number | null>(null);
  const { isConnected, mode, serverStartedAt } = opts;
  useEffect(() => {
    if (!isConnected) {
      setClientAnchor(null);
      return;
    }
    // Capture the anchor ONCE per call, at the moment the automatic
    // path is confirmed. Never overwrite it while the call is live.
    if (mode === 'automatic' && !serverStartedAt) {
      setClientAnchor((prev) => prev ?? Date.now());
    }
  }, [isConnected, mode, serverStartedAt]);

  const serverAnchor = serverStartedAt ? Date.parse(serverStartedAt) : NaN;
  if (Number.isFinite(serverAnchor)) return serverAnchor;
  return clientAnchor;
}

export function ActiveCallRecordingCard({
  callSid,
  workspaceRecordingMode,
  activeCallKind,
  outboundCallId,
}: Props) {
  const isConnected =
    activeCallKind === 'connecting' || activeCallKind === 'in_call';

  const { data: effective } = useGetRecordingEffectiveStateQuery(
    callSid ? { callSid } : (undefined as never),
    { skip: !callSid || !isConnected, pollingInterval: 3000 },
  );

  const rec = useActiveCallRecording({
    callSid,
    workspaceRecordingMode,
    activeCallKind,
    outboundCallId,
    // Auto-start on connect for user-preference Automatic (NOT
    // compliance-locked). Compliance-locked is handled by TwiML on the
    // backend + the existing `workspaceForced` path in the hook — we
    // deliberately skip firing here to avoid double-starting a
    // recording that TwiML already initiated.
    autoStartOnConnect: effective?.mode === 'automatic' && !effective.locked,
  });

  const anchorMs = useElapsedAnchor({
    isConnected,
    mode: effective?.mode,
    serverStartedAt: effective?.startedAt ?? null,
  });
  const liveDuration = useRecordingDuration(anchorMs);

  if (!callSid || !isConnected) return null;

  // Loading state — no effective-state yet.
  if (!effective) {
    return (
      <section
        data-testid="active-call-recording-card"
        aria-busy="true"
        className="rounded-xl border border-slate-200 bg-white shadow-sm p-5"
      >
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Recording
        </div>
        <div className="mt-3 text-sm text-slate-400">Loading…</div>
      </section>
    );
  }

  // 1. Compliance NEVER — informational only.
  if (effective.mode === 'never') {
    return (
      <section
        data-testid="active-call-recording-card"
        data-variant="compliance-never"
        className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-3"
      >
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Recording
        </div>
        <div className="flex items-center gap-2 text-base font-semibold text-slate-800">
          <span aria-hidden>🔒</span>
          <span>Recording Disabled</span>
        </div>
        <p className="text-sm text-slate-600">
          Your workspace does not allow recording.
        </p>
      </section>
    );
  }

  // 2. Compliance ALWAYS (locked automatic) — status + timer, no actions.
  if (effective.mode === 'automatic' && effective.locked) {
    const durationText =
      effective.status === 'recording'
        ? formatDurationClock(liveDuration)
        : effective.status === 'completed'
          ? 'Complete'
          : '00:00';
    return (
      <section
        data-testid="active-call-recording-card"
        data-variant="compliance-always"
        className="rounded-xl border border-red-200 bg-white shadow-sm p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Recording
          </div>
          <div className="text-[11px] font-semibold text-red-800 bg-red-50 border border-red-200 rounded px-2 py-0.5 flex items-center gap-1">
            <span aria-hidden>🔒</span>
            <span>Recording Required</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="w-3.5 h-3.5 rounded-full bg-red-500 animate-pulse shadow-[0_0_0_4px_rgba(239,68,68,0.15)]"
          />
          <span className="text-base font-semibold text-slate-900">
            Recording
          </span>
        </div>
        <div className="text-2xl font-semibold text-slate-900 tabular-nums tracking-tight">
          {durationText}
          <span className="text-xs text-slate-500 font-normal ml-2">
            elapsed
          </span>
        </div>
      </section>
    );
  }

  const status = effective.status;
  const canStart = effective.canStart && rec.status !== 'starting';
  const canStop = effective.canStop && rec.status !== 'stopping';

  const isRecording = status === 'recording' || rec.status === 'recording';
  const isCompleted = status === 'completed';
  const isStarting = rec.status === 'starting';
  const isStopping = rec.status === 'stopping';

  const lastRecordingDuration =
    isCompleted && effective.startedAt && effective.stoppedAt
      ? Math.max(
          0,
          Math.floor(
            (Date.parse(effective.stoppedAt) -
              Date.parse(effective.startedAt)) /
              1000,
          ),
        )
      : 0;

  return (
    <section
      data-testid="active-call-recording-card"
      data-variant={
        isRecording ? 'recording' : isCompleted ? 'stopped' : 'idle'
      }
      className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4"
    >
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        Recording
      </div>

      {/* Status headline + prominent timer */}
      {isStarting ? (
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="w-3.5 h-3.5 rounded-full bg-red-400 animate-pulse"
          />
          <span className="text-base font-semibold text-slate-900">
            Starting…
          </span>
        </div>
      ) : isStopping ? (
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="w-3.5 h-3.5 rounded-full bg-red-400 animate-pulse"
          />
          <span className="text-base font-semibold text-slate-900">
            Stopping…
          </span>
        </div>
      ) : isRecording ? (
        <>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="w-3.5 h-3.5 rounded-full bg-red-500 animate-pulse shadow-[0_0_0_4px_rgba(239,68,68,0.15)]"
            />
            <span className="text-base font-semibold text-slate-900">
              Recording
            </span>
          </div>
          <div className="text-2xl font-semibold text-slate-900 tabular-nums tracking-tight">
            {formatDurationClock(liveDuration)}
            <span className="text-xs text-slate-500 font-normal ml-2">
              elapsed
            </span>
          </div>
        </>
      ) : isCompleted ? (
        <>
          <div className="flex items-center gap-3">
            <span aria-hidden className="w-3.5 h-3.5 rounded-full bg-slate-300" />
            <span className="text-base font-semibold text-slate-900">
              Not Recording
            </span>
          </div>
          {lastRecordingDuration > 0 && (
            <div className="text-xs text-slate-500">
              Last Recording ·{' '}
              <span className="tabular-nums text-slate-700 font-medium">
                {formatDurationHuman(lastRecordingDuration)}
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="flex items-center gap-3">
          <span aria-hidden className="w-3.5 h-3.5 rounded-full bg-slate-300" />
          <span className="text-base font-semibold text-slate-900">
            Not Recording
          </span>
        </div>
      )}

      {rec.error && (
        <div
          data-testid="active-call-recording-error"
          role="alert"
          className="text-xs text-red-700 flex items-center gap-2"
        >
          <span className="flex-1">{rec.error}</span>
          <button
            type="button"
            onClick={rec.dismissError}
            className="underline text-red-700 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Action */}
      {isRecording ? (
        <button
          type="button"
          data-testid="active-call-recording-stop"
          onClick={() => void rec.stop()}
          disabled={!canStop || isStopping}
          className="w-full py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isStopping ? 'Stopping…' : 'Stop Recording'}
        </button>
      ) : isCompleted ? (
        <button
          type="button"
          data-testid="active-call-recording-resume"
          onClick={() => void rec.start()}
          disabled={!canStart || isStarting}
          className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isStarting ? 'Starting…' : 'Resume Recording'}
        </button>
      ) : (
        <button
          type="button"
          data-testid="active-call-recording-start"
          onClick={() => void rec.start()}
          disabled={!canStart || isStarting}
          className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isStarting ? 'Starting…' : 'Start Recording'}
        </button>
      )}
    </section>
  );
}

export default ActiveCallRecordingCard;
